(() => {
  const DETAIL_MOBILE_QUERY = '(max-width: 991px)';

  const parseRal = (value) => {
    if (!value) return Number.POSITIVE_INFINITY;
    const match = value.match(/(\d{3,4})/);
    return match ? Number.parseInt(match[1], 10) : Number.POSITIVE_INFINITY;
  };

  const parseJson = (value) => {
    if (!value) return [];

    try {
      return JSON.parse(value);
    } catch (_error) {
      return [];
    }
  };

  const formatMoney = (cents) => {
    const numericCents = Number.parseInt(cents, 10);
    if (Number.isNaN(numericCents)) return '';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(numericCents, window.money_format);
    }

    return `$${(numericCents / 100).toFixed(2)}`;
  };

  const normalizeValue = (value) => (value || '').trim().toLowerCase();

  const updateUrl = (searchValue, finishValue, sortValue) => {
    const url = new URL(window.location.href);

    if (searchValue) {
      url.searchParams.set('q', searchValue);
    } else {
      url.searchParams.delete('q');
    }

    if (finishValue) {
      url.searchParams.set('finish', finishValue);
    } else {
      url.searchParams.delete('finish');
    }

    if (sortValue && sortValue !== 'featured') {
      url.searchParams.set('sort', sortValue);
    } else {
      url.searchParams.delete('sort');
    }

    window.history.replaceState({}, '', url.toString());
  };

  const getFocusableElements = (container) => {
    if (!container) return [];

    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true');
  };

  class SiliconeColourRange {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('[data-colour-grid]');
      this.cards = Array.from(root.querySelectorAll('[data-colour-card]'));
      this.emptyMessage = root.querySelector('[data-colour-empty-message]');
      this.resultsSummary = root.querySelector('[data-colour-results-summary]');
      this.resultsCount = root.querySelector('[data-colour-results-count]');
      this.activeFilters = root.querySelector('[data-colour-active-filters]');
      this.filterTrigger = root.querySelector('[data-colour-filter-trigger]');
      this.filterSheet = root.querySelector('[data-colour-filter-sheet]');
      this.filterCloseButtons = Array.from(root.querySelectorAll('[data-colour-filter-close]'));
      this.clearFiltersButton = root.querySelector('[data-colour-clear-filters]');
      this.activeFilterCount = root.querySelector('[data-colour-active-filter-count]');
      this.searchInput = root.querySelector('[data-colour-search]');
      this.finishSelect = root.querySelector('[data-colour-finish-filter]');
      this.sortSelect = root.querySelector('[data-colour-sort]');
      this.detail = root.querySelector('[data-colour-modal]');
      this.detailPanel = root.querySelector('[data-colour-detail-panel]');
      this.detailCloseButtons = Array.from(root.querySelectorAll('[data-colour-close-detail], [data-colour-close-modal]'));
      this.productCache = {};
      this.isFilterDrawerOpen = false;
      this.isDetailOpen = false;
      this.lastDetailTrigger = null;
      this.boundMediaQueryChange = this.syncDetailMode.bind(this);
      this.controller = new AbortController();

      if (!this.grid || this.cards.length === 0 || !this.detail || !this.detailPanel) return;

      this.modalName = this.detail.querySelector('[data-modal-name]');
      this.modalRalTargets = Array.from(this.detail.querySelectorAll('[data-modal-ral]'));
      this.modalHex = this.detail.querySelector('[data-modal-hex]');
      this.modalFinishTargets = Array.from(this.detail.querySelectorAll('[data-modal-finish]'));
      this.modalDescription = this.detail.querySelector('[data-modal-description]');
      this.modalImage = this.detail.querySelector('[data-modal-image]');
      this.modalSwatch = this.detail.querySelector('[data-modal-swatch]');
      this.modalSwatchChip = this.detail.querySelector('[data-modal-swatch-chip]');
      this.modalThumbs = this.detail.querySelector('[data-modal-media-thumbs]');
      this.modalPrimaryProduct = this.detail.querySelector('[data-modal-primary-product]');
      this.modalPrimaryHeading = this.detail.querySelector('[data-modal-primary-heading]');
      this.modalRelatedHeading = this.detail.querySelector('[data-modal-related-heading]');
      this.modalRelatedProducts = this.detail.querySelector('[data-modal-related-products]');
      this.detailDescriptionSection = this.detail.querySelector('[data-colour-detail-description]');
      this.detailProductsSection = this.detail.querySelector('[data-colour-detail-products]');
      this.detailMediaMain = this.detail.querySelector('.silicone-colour-detail__media-main');
      this.detailEntryFocus = this.detail.querySelector('[data-colour-detail-close]') || this.detailPanel;

      this.quickAddWrap = this.detail.querySelector('[data-modal-quick-add]');
      this.quickAddProductLink = this.detail.querySelector('[data-modal-product-link]');
      this.quickAddPrice = this.detail.querySelector('[data-modal-product-price]');
      this.quickAddColourName = this.detail.querySelector('[data-modal-colour-callout-name]');
      this.quickAddColourHex = this.detail.querySelector('[data-modal-colour-callout-hex]');
      this.quickAddOptions = this.detail.querySelector('[data-modal-product-options]');
      this.quickAddForm = this.detail.querySelector('[data-modal-add-form]');
      this.quickAddButton = this.detail.querySelector('[data-modal-add-button]');
      this.quickAddStatus = this.detail.querySelector('[data-modal-form-status]');

      this.activeProduct = null;
      this.activeVariant = null;
      this.activeColourName = '';
      this.activeColourHex = '';
      this.activeProductUrl = '';
      this.activeColourOptionIndex = -1;
      this.activeImageList = [];
      this.detailMediaQuery = typeof window.matchMedia === 'function'
        ? window.matchMedia(DETAIL_MOBILE_QUERY)
        : null;

      this.bindBrowseEvents();
      this.bindDetailEvents();
      this.applyInitialQueryState();
      this.syncDetailMode();
      this.applyFiltersAndSort();
    }

    bindBrowseEvents() {
      this.root.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-colour-open-modal]');
        if (!trigger) return;

        const card = trigger.closest('[data-colour-card]');
        if (card) this.openDetail(card, trigger);
      }, { signal: this.controller.signal });

      if (this.searchInput) {
        this.searchInput.addEventListener('input', () => this.applyFiltersAndSort(), { signal: this.controller.signal });
      }

      if (this.finishSelect) {
        this.finishSelect.addEventListener('change', () => this.applyFiltersAndSort(), { signal: this.controller.signal });
      }

      if (this.sortSelect) {
        this.sortSelect.addEventListener('change', () => this.applyFiltersAndSort(), { signal: this.controller.signal });
      }

      if (this.filterTrigger && this.filterSheet) {
        this.filterTrigger.addEventListener('click', () => {
          if (this.isFilterDrawerOpen) {
            this.closeFilterDrawer();
            return;
          }

          this.openFilterDrawer();
        }, { signal: this.controller.signal });
      }

      this.filterCloseButtons.forEach((element) => {
        element.addEventListener('click', () => this.closeFilterDrawer(), { signal: this.controller.signal });
      });

      if (this.clearFiltersButton) {
        this.clearFiltersButton.addEventListener('click', () => {
          this.clearAllFilters();
          this.closeFilterDrawer();
        }, { signal: this.controller.signal });
      }

      if (this.activeFilters) {
        this.activeFilters.addEventListener('click', (event) => this.handleFilterChipClick(event), { signal: this.controller.signal });
      }
    }

    bindDetailEvents() {
      document.addEventListener('keydown', (event) => this.handleDocumentKeydown(event), { signal: this.controller.signal });

      this.detailCloseButtons.forEach((element) => {
        element.addEventListener('click', () => this.closeDetail(), { signal: this.controller.signal });
      });

      if (this.quickAddForm) {
        this.quickAddForm.addEventListener('submit', (event) => this.handleQuickAddSubmit(event), { signal: this.controller.signal });
      }

      if (this.quickAddOptions) {
        this.quickAddOptions.addEventListener('change', () => this.handleOptionChange(), { signal: this.controller.signal });
      }

      if (this.modalThumbs) {
        this.modalThumbs.addEventListener('click', (event) => {
          const thumb = event.target.closest('[data-modal-thumb]');
          if (!thumb || !thumb.dataset.imageSrc) return;

          this.setMainModalImage(thumb.dataset.imageSrc, thumb.dataset.imageAlt || '');
        }, { signal: this.controller.signal });
      }

      if (this.detailMediaQuery && typeof this.detailMediaQuery.addEventListener === 'function') {
        this.detailMediaQuery.addEventListener('change', this.boundMediaQueryChange);
      }
    }

    applyInitialQueryState() {
      const url = new URL(window.location.href);

      if (this.searchInput && url.searchParams.get('q')) {
        this.searchInput.value = url.searchParams.get('q');
      }

      if (this.finishSelect && url.searchParams.get('finish')) {
        this.finishSelect.value = url.searchParams.get('finish');
      }

      if (this.sortSelect && url.searchParams.get('sort')) {
        this.sortSelect.value = url.searchParams.get('sort');
      }
    }

    applyFiltersAndSort() {
      const rawSearchValue = this.searchInput ? this.searchInput.value.trim() : '';
      const rawFinishValue = this.finishSelect ? this.finishSelect.value.trim() : '';
      const searchValue = rawSearchValue.toLowerCase();
      const finishValue = rawFinishValue.toLowerCase();
      const sortValue = this.sortSelect ? this.sortSelect.value : 'featured';

      const visibleCards = this.cards.filter((card) => {
        const name = card.dataset.name || '';
        const ral = card.dataset.ral || '';
        const finish = card.dataset.finish || '';

        const searchMatch = searchValue === '' || name.includes(searchValue) || ral.includes(searchValue);
        const finishMatch = finishValue === '' || finish === finishValue;
        const visible = searchMatch && finishMatch;

        card.hidden = !visible;
        return visible;
      });

      this.sortCards(sortValue, visibleCards);

      if (this.emptyMessage) {
        this.emptyMessage.hidden = visibleCards.length !== 0;
      }

      if (this.resultsSummary) {
        this.resultsSummary.hidden = this.cards.length === 0;
      }

      if (this.resultsCount) {
        this.resultsCount.textContent = `${visibleCards.length}`;
      }

      this.renderActiveFilters(rawSearchValue, rawFinishValue);
      updateUrl(rawSearchValue, rawFinishValue, sortValue);
    }

    renderActiveFilters(searchValue, finishValue) {
      const activeItems = [];
      const searchLabel = this.activeFilters?.dataset.searchLabel || 'Search';
      const finishLabel = this.activeFilters?.dataset.finishLabel || 'Finish';
      const clearLabel = this.activeFilters?.dataset.clearLabel || 'Clear';

      if (searchValue) {
        activeItems.push({ type: 'search', label: `${searchLabel}: ${searchValue}` });
      }

      if (finishValue) {
        activeItems.push({ type: 'finish', label: `${finishLabel}: ${finishValue}` });
      }

      if (this.activeFilters) {
        this.activeFilters.innerHTML = '';

        activeItems.forEach((item) => {
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'silicone-colour-range__filter-chip';
          chip.setAttribute('data-colour-filter-chip', item.type);
          chip.textContent = `${item.label} ${clearLabel}`;
          this.activeFilters.appendChild(chip);
        });

        this.activeFilters.hidden = activeItems.length === 0;
      }

      if (this.activeFilterCount) {
        this.activeFilterCount.textContent = `${activeItems.length}`;
        this.activeFilterCount.hidden = activeItems.length === 0;
      }
    }

    handleFilterChipClick(event) {
      const chip = event.target.closest('[data-colour-filter-chip]');
      if (!chip) return;

      const chipType = chip.getAttribute('data-colour-filter-chip');
      if (chipType === 'search' && this.searchInput) {
        this.searchInput.value = '';
      }

      if (chipType === 'finish' && this.finishSelect) {
        this.finishSelect.value = '';
      }

      this.applyFiltersAndSort();
    }

    clearAllFilters() {
      if (this.searchInput) this.searchInput.value = '';
      if (this.finishSelect) this.finishSelect.value = '';
      if (this.sortSelect) this.sortSelect.value = 'featured';
      this.applyFiltersAndSort();
    }

    openFilterDrawer() {
      if (!this.filterSheet || !this.filterTrigger) return;

      this.isFilterDrawerOpen = true;
      this.root.classList.add('is-filter-drawer-open');
      this.filterSheet.hidden = false;
      this.filterTrigger.setAttribute('aria-expanded', 'true');
      this.updateBodyScrollLock();
    }

    closeFilterDrawer() {
      if (!this.filterSheet || !this.filterTrigger) return;

      this.isFilterDrawerOpen = false;
      this.root.classList.remove('is-filter-drawer-open');
      this.filterSheet.hidden = true;
      this.filterTrigger.setAttribute('aria-expanded', 'false');
      this.updateBodyScrollLock();
    }

    syncDetailMode() {
      if (!this.detailPanel) return;

      const mode = this.detailMediaQuery && this.detailMediaQuery.matches ? 'mobile' : 'desktop';
      this.detailPanel.dataset.detailMode = mode;
    }

    updateBodyScrollLock() {
      document.body.style.overflow = this.isDetailOpen || this.isFilterDrawerOpen ? 'hidden' : '';
    }

    handleDocumentKeydown(event) {
      if (event.key === 'Escape') {
        if (this.isFilterDrawerOpen) {
          this.closeFilterDrawer();
          return;
        }

        if (this.isDetailOpen) {
          this.closeDetail();
        }

        return;
      }

      if (event.key === 'Tab' && this.isDetailOpen) {
        this.handleDetailTabKey(event);
      }
    }

    handleDetailTabKey(event) {
      const focusableElements = getFocusableElements(this.detailPanel);
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && (activeElement === firstElement || activeElement === this.detailPanel)) {
        event.preventDefault();
        lastElement.focus();
      }

      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    sortCards(sortValue, visibleCards) {
      const items = [...visibleCards];

      items.sort((cardA, cardB) => {
        const nameA = cardA.dataset.name || '';
        const nameB = cardB.dataset.name || '';
        const ralA = parseRal(cardA.dataset.ral || '');
        const ralB = parseRal(cardB.dataset.ral || '');
        const featuredA = Number.parseInt(cardA.dataset.featuredIndex || '0', 10);
        const featuredB = Number.parseInt(cardB.dataset.featuredIndex || '0', 10);

        if (sortValue === 'name_asc') return nameA.localeCompare(nameB);
        if (sortValue === 'name_desc') return nameB.localeCompare(nameA);
        if (sortValue === 'ral_asc') return ralA - ralB;
        if (sortValue === 'ral_desc') return ralB - ralA;
        return featuredA - featuredB;
      });

      items.forEach((card) => this.grid.appendChild(card));
    }

    async openDetail(card, trigger) {
      const name = card.dataset.modalName || '';
      const hex = card.dataset.modalHex || '';
      const ral = card.dataset.modalRal || '';
      const finish = card.dataset.modalFinish || '';
      const description = card.dataset.modalDescription || '';
      const image = card.dataset.modalImage || '';
      const primaryTitle = card.dataset.primaryProductTitle || '';
      const primaryUrl = card.dataset.primaryProductUrl || '';
      const primaryHandle = card.dataset.primaryProductHandle || '';
      const relatedProducts = parseJson(card.dataset.relatedProducts || '[]');

      this.lastDetailTrigger = trigger || null;
      this.activeColourName = name;
      this.activeColourHex = hex;
      this.activeProductUrl = primaryUrl;

      if (this.modalName) this.modalName.textContent = name;
      this.modalRalTargets.forEach((element) => {
        element.textContent = ral;
      });
      if (this.modalHex) this.modalHex.textContent = hex;
      this.modalFinishTargets.forEach((element) => {
        element.textContent = finish;
      });
      if (this.modalDescription) this.modalDescription.textContent = description;
      if (this.detailDescriptionSection) this.detailDescriptionSection.hidden = description === '';

      if (this.modalSwatch) {
        this.modalSwatch.style.setProperty('--silicone-colour-value', hex || '#d9d9d9');
      }

      if (this.detailMediaMain) {
        this.detailMediaMain.style.setProperty('--silicone-colour-value', hex || '#d9d9d9');
      }

      if (this.quickAddColourName) this.quickAddColourName.textContent = name;
      if (this.quickAddColourHex) this.quickAddColourHex.textContent = hex;

      if (this.modalSwatchChip) {
        this.modalSwatchChip.textContent = [name, hex].filter(Boolean).join(' ');
        this.modalSwatchChip.hidden = [name, hex].filter(Boolean).length === 0;
      }

      if (this.modalPrimaryProduct) {
        if (primaryTitle && primaryUrl) {
          this.modalPrimaryProduct.textContent = primaryTitle;
          this.modalPrimaryProduct.href = primaryUrl;
          this.modalPrimaryProduct.hidden = false;
          if (this.modalPrimaryHeading) this.modalPrimaryHeading.hidden = false;
        } else {
          this.modalPrimaryProduct.hidden = true;
          if (this.modalPrimaryHeading) this.modalPrimaryHeading.hidden = true;
        }
      }

      if (this.modalRelatedProducts) {
        this.modalRelatedProducts.innerHTML = '';
        relatedProducts.forEach((item) => {
          if (!item.title || !item.url) return;

          const listItem = document.createElement('li');
          const link = document.createElement('a');
          link.href = item.url;
          link.textContent = item.title;
          listItem.appendChild(link);
          this.modalRelatedProducts.appendChild(listItem);
        });

        if (this.modalRelatedHeading) {
          this.modalRelatedHeading.hidden = this.modalRelatedProducts.children.length === 0;
        }
      }

      this.updateProductHierarchyVisibility();

      this.setInitialFallbackImage(image, name);
      this.resetQuickAddStatus('');
      this.showDetail();

      if (primaryHandle) {
        await this.loadQuickAddProduct(primaryHandle, primaryUrl, primaryTitle, image);
      } else {
        this.showQuickAddUnavailable();
      }
    }

    showDetail() {
      this.isDetailOpen = true;
      this.detail.hidden = false;
      this.root.classList.add('is-detail-open');
      this.syncDetailMode();
      this.updateBodyScrollLock();
      document.body.style.overflow = 'hidden';

      if (this.detailEntryFocus && typeof this.detailEntryFocus.focus === 'function') {
        this.detailEntryFocus.focus();
      }
    }

    closeDetail({ restoreFocus = true } = {}) {
      if (!this.detail || this.detail.hidden) return;

      this.isDetailOpen = false;
      this.detail.hidden = true;
      this.root.classList.remove('is-detail-open');
      this.updateBodyScrollLock();
      if (!this.isFilterDrawerOpen) {
        document.body.style.overflow = '';
      }

      if (
        restoreFocus &&
        this.lastDetailTrigger &&
        this.lastDetailTrigger.isConnected &&
        typeof this.lastDetailTrigger.focus === 'function'
      ) {
        this.lastDetailTrigger.focus();
      }
    }

    setInitialFallbackImage(image, colourName) {
      if (this.modalThumbs) {
        this.modalThumbs.innerHTML = '';
      }

      if (image) {
        this.setMainModalImage(image, `${colourName} colour`);
        return;
      }

      if (this.modalImage) {
        this.modalImage.hidden = true;
        this.modalImage.removeAttribute('src');
        this.modalImage.alt = '';
      }
    }

    async loadQuickAddProduct(handle, productUrl, productTitle, fallbackImage) {
      if (!this.quickAddWrap) return;

      try {
        const product = await this.fetchProductByHandle(handle);
        this.activeProduct = product;
        this.activeColourOptionIndex = this.findColourOptionIndex(product, this.activeColourName);

        if (this.quickAddProductLink) {
          this.quickAddProductLink.hidden = false;
          this.quickAddProductLink.href = productUrl || product.url || '#';
          this.quickAddProductLink.textContent = productTitle || product.title;
        }

        this.updateProductHierarchyVisibility();

        if (this.activeColourOptionIndex < 0) {
          this.activeVariant = null;
          if (this.quickAddPrice) this.quickAddPrice.textContent = '';
          if (this.quickAddOptions) this.quickAddOptions.innerHTML = '';
          this.updateAddButtonState();
          this.resetQuickAddStatus('No product variant matches this colour yet.');
          this.renderGallery(fallbackImage);
          return;
        }

        this.activeVariant = this.findInitialVariant(product, this.activeColourName, this.activeColourOptionIndex);
        this.renderOptionSelectors();
        this.refreshVariantUi(fallbackImage);
      } catch (_error) {
        this.activeProduct = null;
        this.activeVariant = null;
        this.showQuickAddUnavailable();
      }
    }

    async fetchProductByHandle(handle) {
      if (this.productCache[handle]) return this.productCache[handle];

      const rootPath = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
      const response = await fetch(`${rootPath}products/${handle}.js`);

      if (!response.ok) {
        throw new Error('Product fetch failed');
      }

      const data = await response.json();
      this.productCache[handle] = data;
      return data;
    }

    findColourOptionIndex(product, colourName) {
      if (!product || !product.options || !product.variants || !colourName) return -1;

      for (let index = 0; index < product.options.length; index += 1) {
        const optionKey = `option${index + 1}`;
        const hasMatch = product.variants.some(
          (variant) => normalizeValue(variant[optionKey]) === normalizeValue(colourName)
        );

        if (hasMatch) return index;
      }

      return -1;
    }

    findInitialVariant(product, colourName, colourOptionIndex) {
      if (!product || !product.variants || product.variants.length === 0) return null;

      if (colourOptionIndex < 0 || !colourName) {
        return product.variants.find((variant) => variant.available) || product.variants[0];
      }

      const optionKey = `option${colourOptionIndex + 1}`;
      const colourVariants = product.variants.filter(
        (variant) => normalizeValue(variant[optionKey]) === normalizeValue(colourName)
      );

      if (colourVariants.length === 0) {
        return null;
      }

      return colourVariants.find((variant) => variant.available) || colourVariants[0];
    }

    renderOptionSelectors() {
      if (!this.quickAddOptions) return;
      this.quickAddOptions.innerHTML = '';

      if (!this.activeProduct || !this.activeProduct.options || this.activeProduct.options.length === 0) return;

      this.activeProduct.options.forEach((optionName, optionIndex) => {
        if (optionIndex === this.activeColourOptionIndex) return;

        const optionKey = `option${optionIndex + 1}`;
        const values = Array.from(new Set(this.activeProduct.variants.map((variant) => variant[optionKey]).filter(Boolean)));

        if (values.length === 0) return;

        const wrapper = document.createElement('label');
        wrapper.className = 'silicone-colour-detail__option';

        const heading = document.createElement('span');
        heading.textContent = optionName;
        wrapper.appendChild(heading);

        const select = document.createElement('select');
        select.name = `option-${optionIndex}`;
        select.setAttribute('data-option-index', `${optionIndex}`);

        values.forEach((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          select.appendChild(option);
        });

        const selectedValue = this.activeVariant ? this.activeVariant[optionKey] : '';
        if (selectedValue) {
          select.value = selectedValue;
        }

        wrapper.appendChild(select);
        this.quickAddOptions.appendChild(wrapper);
      });
    }

    handleOptionChange() {
      if (!this.activeProduct || this.activeColourOptionIndex < 0) return;

      const selectedValues = {};
      this.quickAddOptions.querySelectorAll('select[data-option-index]').forEach((select) => {
        selectedValues[Number.parseInt(select.dataset.optionIndex, 10)] = select.value;
      });

      this.activeVariant = this.activeProduct.variants.find((variant) => {
        for (let index = 0; index < this.activeProduct.options.length; index += 1) {
          const optionKey = `option${index + 1}`;
          const variantValue = variant[optionKey] || '';

          if (index === this.activeColourOptionIndex) {
            if (normalizeValue(variantValue) !== normalizeValue(this.activeColourName)) return false;
          } else if (selectedValues[index] && variantValue !== selectedValues[index]) {
            return false;
          }
        }

        return true;
      }) || null;

      this.refreshVariantUi();
    }

    refreshVariantUi(fallbackImage = '') {
      if (this.quickAddPrice) {
        this.quickAddPrice.textContent = this.activeVariant ? formatMoney(this.activeVariant.price) : '';
      }

      this.renderGallery(fallbackImage);
      this.updateAddButtonState();
    }

    renderGallery(fallbackImage = '') {
      if (!this.modalImage) return;

      const images = [];
      const variantImage = this.activeVariant && this.activeVariant.featured_image
        ? this.activeVariant.featured_image.src
        : '';
      const featuredImage = this.activeProduct && this.activeProduct.featured_image ? this.activeProduct.featured_image : '';
      const productImages = this.activeProduct && Array.isArray(this.activeProduct.images) ? this.activeProduct.images : [];

      if (variantImage) images.push(variantImage);
      if (featuredImage) images.push(featuredImage);
      productImages.forEach((img) => images.push(img));

      const uniqueImages = Array.from(new Set(images.filter(Boolean)));
      this.activeImageList = uniqueImages;

      if (this.modalThumbs) {
        this.modalThumbs.innerHTML = '';
      }

      if (uniqueImages.length > 0) {
        this.setMainModalImage(uniqueImages[0], `${this.activeColourName} colour`);

        if (this.modalThumbs && uniqueImages.length > 1) {
          uniqueImages.forEach((imageSrc, index) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'silicone-colour-detail__thumb';
            button.setAttribute('data-modal-thumb', '');
            button.dataset.imageSrc = imageSrc;
            button.dataset.imageAlt = this.activeColourName;
            if (index === 0) button.classList.add('is-active');

            const image = document.createElement('img');
            image.src = imageSrc;
            image.alt = `${this.activeColourName} thumbnail ${index + 1}`;
            image.loading = 'lazy';

            button.appendChild(image);
            this.modalThumbs.appendChild(button);
          });
        }

        return;
      }

      if (fallbackImage) {
        this.setMainModalImage(fallbackImage, `${this.activeColourName} colour`);
        return;
      }

      this.modalImage.hidden = true;
      this.modalImage.removeAttribute('src');
      this.modalImage.alt = '';
    }

    setMainModalImage(src, alt) {
      if (!this.modalImage) return;

      this.modalImage.src = src;
      this.modalImage.alt = alt;
      this.modalImage.hidden = false;

      if (this.modalThumbs) {
        this.modalThumbs.querySelectorAll('[data-modal-thumb]').forEach((thumb) => {
          thumb.classList.toggle('is-active', thumb.dataset.imageSrc === src);
        });
      }
    }

    updateAddButtonState({ preserveStatus = false } = {}) {
      if (!this.quickAddButton) return;

      if (!this.activeVariant) {
        this.quickAddButton.disabled = true;
        this.resetQuickAddStatus('This variant combination is unavailable.');
        return;
      }

      if (!this.activeVariant.available) {
        this.quickAddButton.disabled = true;
        this.resetQuickAddStatus('This variant is currently sold out.');
        return;
      }

      this.quickAddButton.disabled = false;
      if (!preserveStatus) {
        this.resetQuickAddStatus('');
      }
    }

    async handleQuickAddSubmit(event) {
      event.preventDefault();

      if (!this.activeVariant || !this.quickAddButton || this.quickAddButton.disabled) return;

      const quantityInput = this.quickAddForm ? this.quickAddForm.querySelector('input[name="quantity"]') : null;
      const quantity = quantityInput ? Number.parseInt(quantityInput.value, 10) : 1;
      const parsedQuantity = Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;

      this.quickAddButton.disabled = true;
      this.quickAddButton.classList.add('is-loading');
      this.resetQuickAddStatus('');

      try {
        const payload = {
          id: this.activeVariant.id,
          quantity: parsedQuantity
        };

        const cartNotification = document.querySelector('cart-notification');
        if (cartNotification && typeof cartNotification.getSectionsToRender === 'function') {
          payload.sections = cartNotification.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }

        const response = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(payload)
        });

        const parsedResponse = await response.json();
        if (!response.ok || parsedResponse.status) {
          this.resetQuickAddStatus(parsedResponse.description || 'Unable to add this variant to cart.');
          return;
        }

        if (cartNotification && typeof cartNotification.renderContents === 'function') {
          cartNotification.renderContents(parsedResponse);
        } else {
          this.resetQuickAddStatus('Added to cart.');
        }
      } catch (_error) {
        this.resetQuickAddStatus('Unable to add this variant to cart.');
      } finally {
        this.quickAddButton.classList.remove('is-loading');
        this.updateAddButtonState({ preserveStatus: true });
      }
    }

    resetQuickAddStatus(message) {
      if (!this.quickAddStatus) return;

      this.quickAddStatus.textContent = message;
      this.quickAddStatus.hidden = message === '';
    }

    updateProductHierarchyVisibility() {
      if (!this.detailProductsSection) return;

      const hasPrimary = Boolean(this.modalPrimaryProduct && !this.modalPrimaryProduct.hidden);
      const hasProductLink = Boolean(this.quickAddProductLink && !this.quickAddProductLink.hidden);
      const hasRelated = Boolean(this.modalRelatedProducts && this.modalRelatedProducts.children.length > 0);

      this.detailProductsSection.hidden = !(hasPrimary || hasProductLink || hasRelated);
    }

    showQuickAddUnavailable() {
      if (this.quickAddProductLink) {
        this.quickAddProductLink.hidden = this.activeProductUrl === '';
      }

      if (this.quickAddPrice) {
        this.quickAddPrice.textContent = '';
      }

      if (this.quickAddOptions) {
        this.quickAddOptions.innerHTML = '';
      }

      if (this.quickAddButton) {
        this.quickAddButton.disabled = true;
      }

      this.resetQuickAddStatus('No linked product is available for direct ordering yet.');
      this.updateProductHierarchyVisibility();
    }

    destroy() {
      this.closeDetail({ restoreFocus: false });
      this.closeFilterDrawer();
      this.controller.abort();

      if (this.detailMediaQuery && typeof this.detailMediaQuery.removeEventListener === 'function') {
        this.detailMediaQuery.removeEventListener('change', this.boundMediaQueryChange);
      }

      if (this.root.__siliconeColourRange === this) {
        this.root.__siliconeColourRange = null;
      }
    }
  }

  const init = () => {
    document.querySelectorAll('[data-silicone-colour-range]').forEach((root) => {
      if (!root.__siliconeColourRange) {
        root.__siliconeColourRange = new SiliconeColourRange(root);
      }
    });
  };

  const unload = (event) => {
    const sectionRoot = event.target?.querySelector?.('[data-silicone-colour-range]') || event.target;
    if (sectionRoot?.__siliconeColourRange) {
      sectionRoot.__siliconeColourRange.destroy();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', unload);
})();
